import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { isSuccess } from '@/context/services/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useNotification } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApCurrencyRatesQueryKey,
  useDeleteApCurrencyRateByCurrencyAndDate,
} from 'ap-openapi';
import { useCallback } from 'react';

type Props = {
  onClearMessage: () => void;
};

export const useDeleteCurrency = ({ onClearMessage }: Props) => {
  const { t } = useTranslation();
  const { send } = useErrorTrackingSystem();
  //TODO: Check again when toaster is merged
  const query = useQueryClient();
  const notify = useNotification();
  const setErrorNotification = useErrorInlineNotificationAPI();
  const { mutateAsync: _mutateAsync, error } =
    useDeleteApCurrencyRateByCurrencyAndDate({
      mutation: {
        onSuccess: () => {
          query.invalidateQueries({
            queryKey: getGetApCurrencyRatesQueryKey(),
          });
        },
      },
    });

  return useCallback(
    async (currency: string) => {
      try {
        const resp = await _mutateAsync({
          currency,
        });
        if (isSuccess(resp.status)) {
          onClearMessage();
          notify.success({
            title: t('foreign_currency.currency.delete.success'),
            opts: {
              duration: 3000,
              style: {
                position: 'fixed',
              },
            },
            bodyProps: {
              disabledSuffix: true,
            },
          });
        } else {
          throw error ? error : resp.data;
        }
      } catch (error) {
        if (error instanceof Error) {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        setErrorNotification(
          new Error(t('foreign_currency.currency.delete.error'))
        );
      }
    },
    [_mutateAsync, error, notify, onClearMessage, send, setErrorNotification, t]
  );
};
